/**
 * Lineage expansion (§17): a cited value → its full derivation chain, every node
 * clickable into the peek panel. Renders the in-payload derivation tree
 * immediately, and — when the value carries an `atom_id` — MERGES in the
 * durable cross-run DAG (`evidence_atom_edges`) fetched from the lineage
 * endpoint.
 *
 * Merges, not replaces. The fetch is a progressive enhancement over a card the
 * reader is already reading: it may add ancestry, and it may never take a
 * figure back off the screen. `evidenceService.getAtomLineage` returns `null`
 * on any failure, so a dead endpoint leaves the in-payload tree exactly as it
 * was rather than emptying the section.
 */

import { useEffect, useState } from "react";
import { GitBranch, Loader2 } from "lucide-react";

import type { CitedValue } from "../../types/citationArtifact";
import type { EvidenceLineage } from "../../types/evidenceLineage";
import { evidenceService } from "../../services/evidenceService";
import { SourceChip } from "./SourceChip";
import {
	buildLocalLineage,
	buildRemoteLineage,
	mergeLineage,
	type LineageNode,
} from "./lineage";

const RELATION_LABEL: Record<string, string> = {
	input: "input",
	derived_from: "input",
	input_to: "input",
	supersedes: "supersedes",
};

function LineageNodeRow({
	node,
	depth,
}: {
	node: LineageNode;
	depth: number;
}) {
	return (
		<li className="text-sm">
			<div className="flex items-center gap-2 py-0.5" data-depth={depth}>
				{depth > 0 && (
					<span className="text-xs text-text-muted">
						{RELATION_LABEL[node.relation ?? "input"] ?? "input"} ·
					</span>
				)}
				{node.ref ? (
					<SourceChip source={node.ref} label={node.title} />
				) : (
					<span className="font-medium text-text-strong">{node.title}</span>
				)}
			</div>
			{node.children.length > 0 && (
				<ul className="ml-4 border-l border-gray-200 pl-3">
					{node.children.map((child) => (
						<LineageNodeRow key={child.id} node={child} depth={depth + 1} />
					))}
				</ul>
			)}
		</li>
	);
}

interface LineageViewProps {
	value: CitedValue;
}

export function LineageView({ value }: LineageViewProps) {
	const [remote, setRemote] = useState<EvidenceLineage | null>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		let cancelled = false;
		if (!value.atom_id) {
			setRemote(null);
			return;
		}
		setLoading(true);
		evidenceService
			.getAtomLineage(value.atom_id)
			.then((result) => {
				if (!cancelled) setRemote(result);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [value.atom_id]);

	// Additive by construction: the tree the reader can already see is the
	// base, and the durable DAG only ever adds to it. Swapping one for the
	// other blanked the figures the card had just shown — see `mergeLineage`.
	const localTree = buildLocalLineage(value);
	const remoteTree = remote ? buildRemoteLineage(remote) : null;
	const tree = mergeLineage(localTree, remoteTree);
	const durable = Boolean(remoteTree);

	return (
		<section className="mt-3">
			<header className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-text-muted">
				<GitBranch className="h-3.5 w-3.5" aria-hidden />
				<span>Derivation lineage</span>
				{loading && <Loader2 className="h-3 w-3 animate-spin" aria-hidden />}
				<span className="rounded bg-gray-100 px-1 text-[11px] text-text-muted">
					{durable ? "durable" : "this answer"}
				</span>
				{remote?.truncated && (
					<span className="text-[11px] text-amber-600">(truncated)</span>
				)}
			</header>
			{tree.children.length === 0 ? (
				<p className="text-xs text-text-muted">
					No further lineage recorded for this value.
				</p>
			) : (
				<ul>
					<LineageNodeRow node={tree} depth={0} />
				</ul>
			)}
		</section>
	);
}

export default LineageView;
